import { useQuery } from '@tanstack/react-query'
import { minutesToMillis } from 'helpers'
import useUserContext from 'shared/users/contexts/useUserContext'
import { QueryKeys } from '../../key-registry'
import { ensureLastReadAtSeeded } from '../models/newsLastRead'
import { fetchNewsUnreadCountV1 } from './http'

const POLL_INTERVAL = minutesToMillis(5)

export async function fetchNewsUnreadCount(since: string, opts?: { signal?: AbortSignal }): Promise<number> {
  return fetchNewsUnreadCountV1(since, opts)
}

export function useNewsUnreadCount(): number {
  const { user } = useUserContext()
  const userId = user?.id

  // The key carries the user but no `since` value: the timestamp is read at fetch time, so any
  // refetch — poll, focus, or the invalidation after marking the feed read — picks up the current
  // one. That is also what carries cross-tab consistency, since a second tab re-reads on focus.
  //
  // `staleTime: 0` is load-bearing: v4 gates the focus refetch on isStale(), so any non-zero value
  // would make returning to the tab a no-op inside that window. Request volume is bounded by the
  // interval instead, and the interval is suppressed while the tab is hidden.
  const { data } = useQuery({
    queryKey: QueryKeys.news.unreadCount(userId),
    queryFn: ({ signal }) => fetchNewsUnreadCount(ensureLastReadAtSeeded(userId as number), { signal }),
    enabled: userId !== undefined,
    staleTime: 0,
    refetchInterval: POLL_INTERVAL,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })

  return data ?? 0
}
